import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Code, Eye, Save, Globe } from 'lucide-react';
import Editor from '@monaco-editor/react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const MONACO_OPTIONS = {
  minimap: { enabled: false },
  fontSize: 14,
  wordWrap: 'on',
  lineNumbers: 'on',
  scrollBeyondLastLine: false,
  automaticLayout: true,
  tabSize: 2,
  renderWhitespace: 'selection',
  smoothScrolling: true,
  cursorBlinking: 'smooth',
  padding: { top: 16, bottom: 16 },
};

const WebsiteEditor = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [code, setCode] = useState({
    html: '',
    css: '',
    js: ''
  });

  useEffect(() => {
    fetchWebsite();
  }, [websiteId]);

  const fetchWebsite = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/websites/${websiteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setWebsite(data);
      setCode({
        html: data.html_code || '',
        css: data.css_code || '',
        js: data.js_code || ''
      });
    } catch (error) {
      toast.error('Failed to load website');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/websites/${websiteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          html_code: code.html,
          css_code: code.css,
          js_code: code.js
        })
      });

      if (response.ok) {
        toast.success('Website saved successfully!');
        fetchWebsite();
      } else {
        toast.error('Failed to save website');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/websites/${websiteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_published: !website.is_published })
      });

      if (response.ok) {
        toast.success(website.is_published ? 'Website unpublished' : 'Website published!');
        fetchWebsite();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const handlePreview = async () => {
    try {
      // First save any pending changes
      await handleSave();
      
      // Generate preview HTML from current code
      const fullHTML = getPreviewHTML();
      setPreviewHtml(fullHTML);
      setPreviewMode(true);
    } catch (error) {
      toast.error('Failed to generate preview');
      console.error('Preview error:', error);
    }
  };

  const getPreviewHTML = () => {
    const fullHTML = `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <title>Preview</title>
          <style>${code.css}</style>
        </head>
        <body>
          ${code.html}
          <script>${code.js}</script>
        </body>
      </html>
    `;
    return fullHTML;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-state">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-zinc-400">Loading editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="editor-page">
      {/* Header */}
      <div className="border-b border-white/10 bg-black/50 backdrop-blur-xl">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="back-button"
              variant="ghost"
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-heading font-bold" data-testid="website-name">{website?.name}</h1>
              <p className="text-sm text-zinc-400" data-testid="website-status">
                {website?.is_published ? 'Published' : 'Draft'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => previewMode ? setPreviewMode(false) : handlePreview()}
              data-testid="preview-toggle"
              variant="outline"
              className="border-zinc-800 text-white hover:border-zinc-600 rounded-xl"
            >
              {previewMode ? <Code className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {previewMode ? 'Code' : 'Preview'}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-button"
              className="bg-violet-500 hover:bg-violet-600 text-white rounded-xl"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              onClick={handlePublish}
              data-testid="publish-button"
              className="bg-white text-black hover:bg-zinc-200 rounded-xl font-bold"
            >
              <Globe className="w-4 h-4 mr-2" />
              {website?.is_published ? 'Unpublish' : 'Publish'}
            </Button>
          </div>
        </div>
      </div>

      {/* Editor / Preview */}
      <div className="flex-1 overflow-hidden">
        {previewMode ? (
          <iframe
            data-testid="preview-iframe"
            srcDoc={previewHtml || getPreviewHTML()}
            className="w-full h-full bg-white"
            title="Website Preview"
          />
        ) : (
          <Tabs defaultValue="html" className="h-full flex flex-col">
            <TabsList className="bg-zinc-900 border-b border-white/10 rounded-none justify-start p-2">
              <TabsTrigger value="html" data-testid="tab-html" className="data-[state=active]:bg-violet-500">HTML</TabsTrigger>
              <TabsTrigger value="css" data-testid="tab-css" className="data-[state=active]:bg-violet-500">CSS</TabsTrigger>
              <TabsTrigger value="js" data-testid="tab-js" className="data-[state=active]:bg-violet-500">JavaScript</TabsTrigger>
            </TabsList>
            <TabsContent value="html" className="flex-1 m-0 overflow-hidden" data-testid="html-editor">
              <Editor
                height="100%"
                language="html"
                theme="vs-dark"
                value={code.html}
                onChange={(val) => setCode({ ...code, html: val || '' })}
                options={MONACO_OPTIONS}
              />
            </TabsContent>
            <TabsContent value="css" className="flex-1 m-0 overflow-hidden" data-testid="css-editor">
              <Editor
                height="100%"
                language="css"
                theme="vs-dark"
                value={code.css}
                onChange={(val) => setCode({ ...code, css: val || '' })}
                options={MONACO_OPTIONS}
              />
            </TabsContent>
            <TabsContent value="js" className="flex-1 m-0 overflow-hidden" data-testid="js-editor">
              <Editor
                height="100%"
                language="javascript"
                theme="vs-dark"
                value={code.js}
                onChange={(val) => setCode({ ...code, js: val || '' })}
                options={MONACO_OPTIONS}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default WebsiteEditor;